import { ProductType } from 'components/common/CommonTypes';
import LoadingContainer from 'components/common/loadingContainer/LoadingContainer';
import PageTitle from 'components/common/PageTitle';
import ProgressButton from 'components/common/ProgressButton';
import { RoutePaths } from 'Constants/Common';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ClaimApi, GapClaimDocument } from '../../../api/ClaimApi';
import { RepairRequest } from '../../../api/RepairRequestTypes';
import FormInput from '../../common/FormInput';
import {
    allowUploadStatuses,
    DocumentStatus as Status,
    GapClaimDocumentStatusText as StatusText
} from '../../../components/common/policyholderinfo/PolicyHolderInfoTypes';
import DocumentUploader from '../../common/fileUpload/DocumentUploader';
import { useClaimDocumentsUpload } from '../../hooks/useClaimDocumentsUpload';
import { useAppSelector } from 'state/hooks';

type LocationState = {
    product: ProductType;
    repairRequest: RepairRequest;
};

const ClaimDocumentsPage = () => {
    const { getAccessTokenSilently } = useAuth0();
    const [loading] = useState(false);
    const [documents, setDocuments] = useState<GapClaimDocument[]>([]);

    const location = useLocation();
    const { repairRequest: { claimId, referenceNumber } = {} } =
        location.state as LocationState;

    const product = useAppSelector((s) => s.vehicles.selectedPolicy);

    const { onDocumentUploaded, uploadedDocuments, setUploadedDocuments } =
        useClaimDocumentsUpload({
            claimRequestId: String(claimId),
            productReference: product.reference
        });

    useEffect(() => {
        async function fetchData() {
            const token = await getAccessTokenSilently();

            const onLoadDocuments = (documents: GapClaimDocument[]) => {
                setUploadedDocuments(
                    new Array(documents.length).fill(undefined)
                );
                setDocuments(documents);
            };

            new ClaimApi().getClaimDocuments(claimId, token, onLoadDocuments);
        }

        fetchData();
    }, []);

    const getDocumentStatus = (uploaded, statusId: number) =>
        uploaded
            ? StatusText[Status.UploadedPending]
            : (StatusText[statusId] ?? StatusText[Status.NotUploaded]);

    const isUploadSuccessfull = (uploaded, statusId: Status) =>
        uploaded ? false : statusId === Status.UploadedAccepted;

    return (
        <LoadingContainer loading={loading} message={t('loading')}>
            <div className='card page-content'>
                <PageTitle title={`${referenceNumber} - Claim Documents`} />
            </div>
            <div className='card-body page-content-body' id='documents-section'>
                <FormInput id='document-section' label='Documents'>
                    <ul className='list-group list-group-flush'>
                        {documents?.map((document, index) => {
                            const uploadedDocument = uploadedDocuments[index];
                            return (
                                <DocumentUploader
                                    id={`document-${index}`}
                                    key={`document-${index}`}
                                    documentIndex={index}
                                    documentName={document.documentTitle}
                                    documentTypeId={document.documentTypeId}
                                    uploadedDocument={uploadedDocument}
                                    allowUpload={
                                        allowUploadStatuses.includes(
                                            document.documentStatusId
                                        ) && !uploadedDocument
                                    }
                                    status={getDocumentStatus(
                                        uploadedDocument,
                                        document.documentStatusId
                                    )}
                                    success={isUploadSuccessfull(
                                        uploadedDocument,
                                        document.documentStatusId
                                    )}
                                    helpText={document.documentDescription}
                                    // TODO: concidet requestiong the list after uploading
                                    onDocumentUploaded={onDocumentUploaded}
                                />
                            );
                        })}
                    </ul>
                </FormInput>
            </div>
            <div className='bottom-navigation'>
                <ProgressButton
                    navRoute={RoutePaths.ViewRequests}
                    buttonStyle='btn btn-secondary progress-button'
                    text={t('back')}
                />
            </div>
        </LoadingContainer>
    );
};

export default ClaimDocumentsPage;
